import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Lock, Eye, EyeOff, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useUser();
  
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const userData = await login(formData.email, formData.password);
        toast.success('Bentornato!');
        // If admin, redirect to admin dashboard directly
        if (userData.role === 'admin') {
          // Small delay to ensure token is saved
          setTimeout(() => {
            window.location.href = '/admin/dashboard';
          }, 100);
          return;
        }
      } else {
        if (!formData.name) {
          toast.error('Inserisci il tuo nome');
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.name);
        toast.success('Account creato con successo!');
      }
      navigate(from, { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'autenticazione';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <span className="text-3xl font-light tracking-[0.3em] text-black">QANTRI</span>
            </Link>
            <h1 className="text-2xl font-light mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              {isLogin ? 'Bentornato' : 'Crea il tuo account'}
            </h1>
            <p className="text-neutral-500 text-sm">
              {isLogin 
                ? 'Accedi per gestire i tuoi ordini e guadagnare con i referral' 
                : 'Unisciti a QANTRI per un\'esperienza di shopping esclusiva'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="text-sm font-medium text-neutral-700 mb-1 block">Nome</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                  <Input
                    type="text"
                    placeholder="Il tuo nome"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 h-12 rounded-xl"
                    data-testid="register-name"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                <Input
                  type="email"
                  placeholder="email@esempio.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-12 rounded-xl"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-10 h-12 rounded-xl"
                  required
                  minLength={6}
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {isLogin && (
                <div className="text-right">
                  <Link 
                    to="/reset-password" 
                    className="text-sm text-[#064E3B] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Password dimenticata?
                  </Link>
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#064E3B] hover:bg-[#043927] text-white rounded-xl"
              data-testid="login-submit"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  {isLogin ? 'Accedi' : 'Registrati'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-neutral-500 text-sm">
              {isLogin ? 'Non hai un account?' : 'Hai già un account?'}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="ml-1 text-[#064E3B] hover:underline font-medium"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? 'Registrati' : 'Accedi'}
              </button>
            </p>
          </div>

          <div className="mt-8 pt-8 border-t border-neutral-200">
            <p className="text-xs text-neutral-400 text-center">
              Continuando, accetti i nostri{' '}
              <Link to="/terms" className="underline">Termini di Servizio</Link>
              {' '}e la{' '}
              <Link to="/faq" className="underline">Privacy Policy</Link>
            </p>
          </div>
        </motion.div>
      </div>

      {/* Right Side - Image/Info */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#064E3B] to-[#0A6B4F] items-center justify-center p-12">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="text-white text-center max-w-md"
        >
          <h2 className="text-4xl font-light mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Il meglio, <span className="italic">selezionato per te</span>
          </h2>
          <p className="text-white/70 mb-8">
            Scopri prodotti selezionati, guadagna con i referral e accedi a contenuti esclusivi.
          </p>
          
          <div className="space-y-4 text-left">
            <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
              <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center text-black font-bold">
                €5
              </div>
              <div>
                <p className="font-medium">Programma Referral</p>
                <p className="text-sm text-white/60">Guadagna per ogni amico invitato</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                ⭐
              </div>
              <div>
                <p className="font-medium">Punti Fedeltà</p>
                <p className="text-sm text-white/60">Accumula punti ad ogni acquisto</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                📰
              </div>
              <div>
                <p className="font-medium">Contenuti Esclusivi</p>
                <p className="text-sm text-white/60">Magazine e Academy riservati</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
